import { connectRefinementList } from "instantsearch.js/es/connectors";
import { curatedFacetRefinementsSpacer, facetNoResultsLabel } from "../curated-layout";
import { curatedSearchConfig } from "@/services/algoliaRouting.blade";
import { escapeHtml, runAfterFrame } from "./helpers";
import {
    modelFamilyFacetFetchLimit,
    familyMatchesQuery,
    familyToggleValues,
    groupModelFacetItems,
    modelFamilies,
    toggleFacetValuesOnce,
} from "../model-families";

const MODEL_FACET_LIMIT = 10;
const MODEL_FACET_SHOW_MORE_LIMIT = 100;

interface ModelFacetItem {
    value: string;
    label: string;
    count: number;
    isRefined: boolean;
}

const MODEL_FACET_CONTAINER_SELECTORS = [
    "#action-bar-model",
    "#refinement-model",
] as const;

function getModelFacetContainers(): HTMLElement[] {
    return MODEL_FACET_CONTAINER_SELECTORS.flatMap((selector) => {
        const el = document.querySelector<HTMLElement>(selector);
        return el ? [el] : [];
    });
}

function resolveSelectedModels(): Set<string> {
    const selected = new Set<string>();

    const curated = curatedSearchConfig.value.filters?.refinementList?.model;
    if (Array.isArray(curated)) {
        curated.forEach((value) => {
            if (value) selected.add(value);
        });
    }

    if (selected.size === 0) {
        const landingModels =
            window.__MARKETPLACE_CONFIG__?.landingData
                ?.concentratedModelDesignations ?? [];
        landingModels.forEach((value) => {
            if (value) selected.add(value);
        });
    }

    if (selected.size === 0) {
        (
            window.__MARKETPLACE_CONFIG__?.initialModelsFacet ?? []
        ).forEach((item) => {
            if (item.isRefined && item.value) {
                selected.add(item.value);
            }
        });
    }

    return selected;
}

/**
 * Model facet with SSR-frozen options (parity with createMakeMenuWidget).
 * Keeps landing hub + concentration satellites visible and pre-selected on first paint.
 */
export function createModelRefinementWidget() {
    let localSearchQuery = "";
    let listenersBound = false;
    let latestRefine: ((value: string) => void) | null = null;
    // Une ligne par famille (« Cayenne ») : désignations choisies et connues au dernier rendu
    let latestRefinedValues: string[] = [];
    let latestKnownValues: string[] = [];
    // Sans famille renseignée, rien ne change ; sinon toutes les valeurs sont demandées et « Voir plus »
    // coupe la liste des familles (une famille additionne des générations moins fournies)
    const groupFamilies = Object.keys(modelFamilies()).length > 0;
    let showAllFamilies = false;

    const scheduleFacetRender = (
        renderOptions: Parameters<
            Parameters<typeof connectRefinementList>[0]
        >[0],
    ) => {
        (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: { scheduleRender: () => void };
            }
        ).instantSearchInstance.scheduleRender();
    };

    const frozenModels: ModelFacetItem[] = (
        window.__MARKETPLACE_CONFIG__?.initialModelsFacet ?? []
    ).map((item) => ({ ...item }));

    return connectRefinementList((renderOptions, isFirstRender) => {
        const containers = getModelFacetContainers();
        if (!containers.length) return;

        const {
            items: rawItems,
            refine,
            searchForItems,
            isShowingMore,
            canToggleShowMore,
            toggleShowMore,
        } = renderOptions;
        latestRefine = refine;

        const selectedModels = resolveSelectedModels();
        const liveByValue = new Map(rawItems.map((item) => [item.value, item]));

        const mergedItems: ModelFacetItem[] = frozenModels.length
            ? frozenModels.map((frozen) => {
                  const live = liveByValue.get(frozen.value);
                  return {
                      value: frozen.value,
                      label: frozen.label,
                      count: live?.count ?? frozen.count,
                      isRefined: selectedModels.has(frozen.value),
                  };
              })
            : rawItems.map((item) => ({
                  value: item.value,
                  label: item.label,
                  count: item.count,
                  isRefined: selectedModels.has(item.value),
              }));

        for (const live of rawItems) {
            if (mergedItems.some((item) => item.value === live.value)) {
                continue;
            }
            mergedItems.push({
                value: live.value,
                label: live.label,
                count: live.count,
                isRefined: selectedModels.has(live.value),
            });
        }

        const helperState = (
            renderOptions as typeof renderOptions & {
                instantSearchInstance: {
                    mainIndex?: { getHelper?: () => { state?: { disjunctiveFacetsRefinements?: Record<string, string[]> } } | null };
                };
            }
        ).instantSearchInstance.mainIndex?.getHelper?.()?.state;
        latestRefinedValues = [
            ...new Set([
                ...(helperState?.disjunctiveFacetsRefinements?.model ?? []),
                ...mergedItems.filter((item) => item.isRefined).map((item) => item.value),
            ]),
        ];
        latestKnownValues = mergedItems.map((item) => item.value);
        const groupedItems = groupModelFacetItems(mergedItems);
        const familyShowMore = groupFamilies && groupedItems.length > MODEL_FACET_LIMIT;
        // Toutes les générations additionnées (toutes), puis la liste des
        // familles coupée comme l'ancienne liste (10, puis 100 avec « Voir plus »)
        const familyDisplayCap = familyShowMore && !showAllFamilies ? MODEL_FACET_LIMIT : MODEL_FACET_SHOW_MORE_LIMIT;
        const familyItems =
            groupFamilies && localSearchQuery.trim() === ""
                ? groupedItems.slice(0, familyDisplayCap)
                : groupedItems;

        const q = localSearchQuery.trim().toLowerCase();
        // Regroupée : la recherche porte sur le nom de la famille et sur ses désignations
        const displayItems = q
            ? familyItems.filter((item) =>
                  groupFamilies
                      ? familyMatchesQuery(item.value, q, latestKnownValues)
                      : item.label.toLowerCase().includes(q),
              )
            : familyItems;
        const hasSelected = familyItems.some((item) => item.isRefined);

        const listHtml = displayItems.length
            ? displayItems
                  .map(
                      (item) =>
                          `<label data-attr="listing-panel-option" class="RefinementList${
                              item.isRefined
                                  ? " RefinementList--selected"
                                  : ""
                          }">` +
                          `<span><span class="RefinementList__labelText">${escapeHtml(
                              item.label,
                          )}</span>` +
                          `<span class="RefinementList__count">(${item.count.toLocaleString(
                              "fr-FR",
                          )})</span></span>` +
                          `<input type="checkbox" class="RefinementList__checkbox"` +
                          `${
                              item.isRefined ? " checked" : ""
                          } data-value="${escapeHtml(
                              item.value,
                          )}" /></label>`,
                  )
                  .join("")
            : `<span class="no-results-label">${facetNoResultsLabel()}</span>`;

        const showMoreToggle = groupFamilies ? familyShowMore : canToggleShowMore;
        const showingMore = groupFamilies ? showAllFamilies : isShowingMore;
        const showMoreHtml = showMoreToggle
            ? `<button type="button" class="RefinementList__showMore" data-attr="listing-panel-show-more">${
                  showingMore ? "Voir moins" : "Voir plus"
              }</button>`
            : "";

        const markup =
            `<div class="RefinementList__search input__field input__field--facette">` +
            `<input type="search" class="input input--search" data-attr="listing-panel-search" placeholder="Rechercher un modèle…" autocomplete="off" data-model-facet-search /></div>` +
            curatedFacetRefinementsSpacer("model") +
            `<div class="RefinementList__wrapper${
                hasSelected ? " RefinementList__wrapper--has-selected" : ""
            }">${listHtml}${showMoreHtml}</div>`;

        for (const container of containers) {
            if (isFirstRender) {
                container.innerHTML = markup;
            } else {
                const wrap = container.querySelector<HTMLElement>(
                    ".RefinementList__wrapper",
                );
                if (wrap) {
                    wrap.classList.toggle(
                        "RefinementList__wrapper--has-selected",
                        hasSelected,
                    );
                    wrap.innerHTML = listHtml + showMoreHtml;
                }
            }
        }

        if (!listenersBound && isFirstRender) {
            listenersBound = true;

            // Bind search on every mount (sidebar + action-bar). Listening only
            // on containers[0] left the action-bar input inert when it was the
            // secondary selector — which is the one users type in.
            const onModelSearchInput = (event: Event) => {
                const input = event.target as HTMLInputElement;
                if (
                    !(input instanceof HTMLInputElement) ||
                    !input.matches("[data-model-facet-search]")
                ) {
                    return;
                }
                localSearchQuery = input.value;
                containers.forEach((container) => {
                    const mirror = container.querySelector<HTMLInputElement>(
                        "[data-model-facet-search]",
                    );
                    if (mirror && mirror !== input) {
                        mirror.value = input.value;
                    }
                });
                // Regroupée : toutes les valeurs sont déjà là, la recherche est locale (familles et
                // désignations) ; la recherche distante ne connaît que les désignations
                if (!groupFamilies) searchForItems(localSearchQuery);
                // Re-run the connector so `displayItems` client filter applies
                // immediately; `searchForItems` alone may not re-render until
                // the facet API responds (and SSR-frozen items stay unfiltered).
                scheduleFacetRender(renderOptions);
            };

            containers.forEach((container) => {
                container.addEventListener("input", onModelSearchInput);
            });

            // Same event split as createMenuStyleRefinementWidget: refine on
            // `change` only, deferred past the next paint (INP). A `click`
            // refine would double-fire now that the input survives the tap.
            const onShowMoreClick = (event: Event) => {
                const target = event.target as HTMLElement;
                if (target.classList.contains("RefinementList__showMore")) {
                    if (groupFamilies) {
                        showAllFamilies = !showAllFamilies;
                        runAfterFrame(() => scheduleFacetRender(renderOptions));
                        return;
                    }
                    runAfterFrame(() => toggleShowMore());
                }
            };
            const onCheckboxChange = (event: Event) => {
                const input = event.target as HTMLInputElement;
                if (!input.classList.contains("RefinementList__checkbox")) {
                    return;
                }
                const val = input.dataset.value;
                if (!val) return;
                const values = familyToggleValues(val, latestRefinedValues, latestKnownValues);
                // Toutes les désignations de la famille en un seul changement d'état (une recherche, une navigation)
                runAfterFrame(() =>
                    toggleFacetValuesOnce(
                        (renderOptions as typeof renderOptions & {
                            instantSearchInstance?: Parameters<typeof toggleFacetValuesOnce>[0];
                        }).instantSearchInstance,
                        "model",
                        values,
                        (value) => latestRefine?.(value),
                    ),
                );
            };

            containers.forEach((container) => {
                container.addEventListener("change", onCheckboxChange);
                container.addEventListener("click", onShowMoreClick);
            });
        }
    })({
        attribute: "model",
        operator: "or",
        limit: groupFamilies ? modelFamilyFacetFetchLimit() : MODEL_FACET_LIMIT,
        showMore: !groupFamilies,
        showMoreLimit: MODEL_FACET_SHOW_MORE_LIMIT,
        sortBy: ["isRefined", "count:desc"],
    });
}
